import React from 'react';
import { FiClock, FiMonitor, FiDownload, FiRefreshCw, FiSearch } from 'react-icons/fi';

export const QuickPriceListFilters = ({
  query, onQueryChange, formatOptions, formatCode, onFormatChange,
  categoryOptions, categoryId, onCategoryChange,
  branchId, branches = [], branchesLoading, branchesError, onBranchChange,
}) => (
  <div data-testid="quick-price-filters" className="space-y-2">
    <div className="flex items-center gap-2">
      <label className="relative block min-w-0 flex-1">
        <span className="sr-only">Buscar producto, variedad, formato o código</span>
        <FiSearch aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
          placeholder="Buscar producto..."
          className="h-9 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </label>
      <label className="block w-2/5 min-w-0 max-w-64">
        <span className="sr-only">Sucursal de la lista de precios</span>
        <select value={branchId} onChange={(event) => onBranchChange(event.target.value)}
          disabled={branchesLoading || branches.length < 2 || Boolean(branchesError)}
          className="h-9 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
          {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branchName}</option>)}
        </select>
      </label>
    </div>
    {branchesError && <p role="status" className="m-0 text-xs text-amber-700">No se pudieron cargar las sucursales. Recarga la página para reintentar.</p>}
    <div className="grid grid-cols-2 gap-2">
      <fieldset className="min-w-0 flex-1">
        <legend className="sr-only">Formatos</legend>
        <div className="flex min-h-9 flex-wrap overflow-hidden rounded-md border border-slate-200 bg-slate-50">
          {formatOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              title={option.label}
              aria-pressed={formatCode === option.value}
              onClick={() => onFormatChange(formatCode === option.value ? '' : option.value)}
              className={`min-h-9 min-w-0 max-w-[12rem] flex-1 truncate border-r border-slate-200 px-2 py-1.5 text-xs font-semibold transition last:border-r-0 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${formatCode === option.value
                ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="block min-w-0">
        <span className="sr-only">Categoría</span>
        <select
          aria-label="Categoría"
          value={categoryId}
          onChange={(event) => onCategoryChange(event.target.value)}
          className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm font-medium text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        >
          <option value="">Categoría</option>
          {categoryOptions.map((category) => <option key={category.id} value={category.id}>{category.label}</option>)}
        </select>
      </label>
    </div>
  </div>
);

const QuickPriceListHeader = React.forwardRef(({ children, loadingLabel, canUpdate, recalculating, recalculationDisabled,
  recalculationTitle, onProjections, onHistory, onPdf, onRecalculate, pendingRecalculationCount = 0 }, ref) => (
  <section ref={ref} className="sticky top-14 z-30 rounded-xl border border-slate-200 bg-white shadow-sm md:top-0">
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-slate-200 px-3 py-2">
      <h1 className="m-0 text-lg font-bold tracking-tight text-slate-950 sm:text-xl">Lista de precios</h1>
      <div data-testid="quick-price-primary-actions" className="flex flex-wrap items-center gap-y-1 divide-x divide-slate-200">
        <button type="button" aria-label="Historial de precios temporales" onClick={onHistory}
          className="inline-flex min-h-8 items-center gap-1.5 pr-2 text-xs font-semibold text-blue-600 hover:text-blue-800 sm:pr-3 sm:text-sm">
          <FiClock aria-hidden="true" className="h-4 w-4" />Historial
        </button>
        {onProjections && <button type="button" aria-label="Configurar proyecciones" onClick={onProjections}
          className="inline-flex min-h-8 items-center gap-1.5 px-2 text-xs font-semibold text-blue-600 hover:text-blue-800 sm:px-3 sm:text-sm">
          <FiMonitor aria-hidden="true" className="h-4 w-4" />Proyecciones
        </button>}
        <button type="button" aria-label="Descargar PDF" onClick={onPdf}
          className="inline-flex min-h-8 items-center gap-1.5 px-2 text-xs font-semibold text-blue-600 hover:text-blue-800 sm:px-3 sm:text-sm">
          <FiDownload aria-hidden="true" className="h-4 w-4" />PDF
        </button>
        {canUpdate && <button type="button" aria-label={recalculating ? 'Recalculando…' : 'Recalcular precios'}
          disabled={recalculationDisabled} title={recalculationTitle} onClick={onRecalculate}
          className={`inline-flex min-h-8 items-center gap-1.5 rounded px-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 sm:text-sm ${pendingRecalculationCount > 0 ? 'bg-amber-50 text-amber-800 hover:bg-amber-100' : 'text-slate-500'}`}>
          <FiRefreshCw aria-hidden="true" className={`h-4 w-4 ${recalculating ? 'animate-spin' : ''}`} />
          {recalculating ? 'Recalculando…' : 'Recalcular'}
        </button>}

      </div>
    </div>
    {loadingLabel && <div role="status" className="flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700">
      <FiRefreshCw aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin" />
      <span>{loadingLabel}</span>
    </div>}
    {children}
    {!canUpdate && <p className="m-0 border-t border-slate-100 px-4 py-3 text-xs font-semibold text-slate-500 sm:px-6">
      Precios regulares de solo lectura. Se editan en la sucursal principal con permiso price.update.
    </p>}
  </section>
));

QuickPriceListHeader.displayName = 'QuickPriceListHeader';

export default QuickPriceListHeader;
